import axios from '../config/axios';

const API_URL = `${(
  process.env.REACT_APP_API_URL ||
  'https://backend-clinical-intake.dizzaroo.com'
).replace(/\/$/, '')}/api/documentEditor/usdm-catalogue`;

const protocolUsdmCatalogueService = {
  // Upload a USDM catalogue JSON file (usdm-schema.json shape, values filled
  // in) for a project. Returns { success, id, classCount, instanceCount }.
  uploadCatalogue: async (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/upload/${projectId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to upload USDM catalogue');
    }
  },

  // Fetches this study's USDM straight from the Data Platform (filtered to
  // the document produced by the Study Design app) and stores it exactly
  // like uploadCatalogue does. Pass a versionId (from listStudyDesignVersions)
  // to fetch that specific published version instead of always the latest.
  // Returns the same shape as uploadCatalogue plus
  // { source, documentId, versionId, versionLabel, isLatestVersion }.
  fetchFromStudyDesign: async (projectId, versionId = null) => {
    try {
      // Body must be a JSON object/array, never `null` — this axios instance
      // defaults to Content-Type: application/json, which serializes a
      // `null` body to the literal string "null"; Express's json() body
      // parser runs in strict mode and rejects that root-level primitive
      // with a 400 ("Unexpected token 'n', \"null\" is not valid JSON").
      const response = await axios.post(`${API_URL}/fetch-from-study-design/${projectId}`, {}, {
        params: versionId ? { versionId } : undefined,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to fetch USDM catalogue from Study Design'
      );
    }
  },

  // Lists every published version of this study's Study Design USDM document,
  // newest first, each with { versionId, versionLabel, status, isLatest,
  // uploadedAt, publishedAt } — used to populate a "fetch by version" picker.
  // Returns [] (not an error) when Study Design isn't linked or has no
  // published USDM yet, so callers can just hide the picker rather than
  // branch on error shapes.
  listStudyDesignVersions: async (projectId) => {
    if (!projectId) return [];
    try {
      const response = await axios.get(`${API_URL}/study-design-versions/${projectId}`);
      return Array.isArray(response.data?.versions) ? response.data.versions : [];
    } catch (error) {
      return [];
    }
  },

  // Mirrors clinicalIntake.service.js's getLatestClinicalIntake: throws a
  // specifically-messaged error on 404 so callers can branch on "no
  // catalogue uploaded yet" the same way they branch on "no intake form".
  getLatestCatalogue: async (projectId) => {
    if (!projectId) {
      throw new Error('projectId is required to fetch the USDM catalogue for the current study');
    }
    try {
      const response = await axios.get(`${API_URL}/latest/${projectId}`);
      return response.data.data;
    } catch (error) {
      if (
        error.response &&
        error.response.status === 404 &&
        error.response.data?.message === 'No USDM catalogue for current project'
      ) {
        throw new Error('No USDM catalogue for current project');
      }
      throw error;
    }
  },

  // Same endpoint as getLatestCatalogue but returns the full envelope
  // (sourceVersionLabel, sourceIsLatestVersion, filename, uploadedAt) instead
  // of just the raw USDM — used to show which version is currently loaded.
  // Returns null (not an error) when there's no catalogue yet.
  getLatestCatalogueMeta: async (projectId) => {
    if (!projectId) return null;
    try {
      const response = await axios.get(`${API_URL}/latest/${projectId}`);
      return response.data;
    } catch (error) {
      return null;
    }
  },

  // Per-field "Use Template" checkbox state + generated text (the USDM
  // counterpart of ClinicalIntakeForm.jsx's per-field template checkboxes —
  // see protocol/utils/usdmTemplateFieldGeneration.js), stored on the latest
  // catalogue doc. Returns {} for both (not an error) when no catalogue has
  // been uploaded yet or nothing has been set.
  getTemplateFieldState: async (projectId) => {
    if (!projectId) return { templateFieldSelections: {}, templateFieldValues: {} };
    try {
      const response = await axios.get(`${API_URL}/latest/${projectId}`);
      return {
        templateFieldSelections: response.data?.templateFieldSelections || {},
        templateFieldValues: response.data?.templateFieldValues || {},
      };
    } catch (error) {
      return { templateFieldSelections: {}, templateFieldValues: {} };
    }
  },

  // Body: { fieldName, checked, value }. Applies to the latest catalogue doc
  // — same "latest wins" convention as getLatestCatalogue.
  updateTemplateFieldState: async (projectId, fieldName, checked, value) => {
    try {
      const response = await axios.put(`${API_URL}/template-field-state/${projectId}`, {
        fieldName,
        checked,
        value,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to save template field state');
    }
  },
};

export default protocolUsdmCatalogueService;
